import React, { useState, useEffect, useRef } from 'react';
import { Star, Clock, Check, Briefcase, Quote, ChevronLeft, ChevronRight, Linkedin } from 'lucide-react';
import { motion } from 'framer-motion';

interface TestimonialItem {
    name: string;
    role: string;
    quote: string;
    time: string;
    result: string;
    company: string;
    /** Logro destacado arriba del comentario (negrita, más grande) */
    achievement?: string;
    videoUrl?: string;
    videoLabel?: string;
    linkedinUrl?: string;
}

const testimonials: TestimonialItem[] = [
    {
        name: 'Camila Cardenal',
        role: 'De pastelera a AI Engineer en 4 meses',
        achievement: 'Consiguió trabajo como AI Engineer en 4 meses',
        quote: '"De trabajar en pesos en Argentina a trabajar en dólares para US. Tu futuro se construye con las decisiones que hoy te animás a tomar. De pastelera a AI Engineer: con trabajo, dedicación y constancia… todo se puede."',
        time: '4 meses',
        result: 'AI Engineer Jr remoto',
        company: 'Trabajo en USD para US',
        videoUrl: 'https://www.linkedin.com/posts/camila-cardenal-451808181_nuevocomienzo-aiengineer-cleartech-activity-7413927672034000896-KAtl',
        videoLabel: 'Ver publicación',
        linkedinUrl: 'https://www.linkedin.com/in/camila-cardenal-451808181/'
    },
    {
        name: 'Matías B.',
        role: 'Mentorías: comunicación y valor',
        achievement: 'Consiguió un 50% más en una empresa para US',
        quote: '"Con las mentorías mejoré mi habilidad de comunicación y entendí mi valor. Conseguí un 50% más en mano."',
        time: 'Con mentorías',
        result: '+50% en mano',
        company: 'Mejor comunicación',
        videoUrl: 'https://www.tiktok.com/@lucianoargolo0/video/7608273245288107271',
        videoLabel: 'Ver en TikTok',
        linkedinUrl: ''
    },
    {
        name: 'Mauro Cortellezzi',
        role: 'Data Engineer en Tekton Labs',
        achievement: 'Consiguió trabajo como Data Engineer en 2 semanas',
        quote: '"Conseguí trabajo en Tekton Labs gracias a tus mentorías. Hice los cambios de LinkedIn el lunes, el miércoles ya me llegaron propuestas. Estuve 1-2 semanas sin que me hable ningún reclutador y esta semana me hablaron dos. Lo que hablamos en las entrevistas me recontra sirvió, usé todo lo que me dijiste. Las preguntas de SQL las saqué de una por haberlo practicado. Me ayudaste un montón."',
        time: '1 semana',
        result: 'Múltiples ofertas',
        company: 'Tekton Labs',
        videoUrl: 'https://www.linkedin.com/posts/luciano-argolo_felicidades-mauro-cortellezzi-por-tu-nuevo-activity-7392584149288546305-11F_',
        linkedinUrl: 'https://www.linkedin.com/in/mauro-cortellezzi-18112281/'
    },
    {
        name: 'Ramiro Crespi',
        role: 'Jr Data Engineer - 20 años',
        achievement: 'Consiguió trabajo como Data Engineer en 2 meses',
        quote: '"Me ayudó a definir bien mis prioridades y entender por dónde ir. Pero lo más importante fue que me dio la confianza para estar cómodo en las entrevistas. A los 2 meses conseguí mi primer trabajo. Muy agradecido!"',
        time: '2 meses',
        result: 'Primer trabajo tech',
        company: 'Data Engineer',
        videoUrl: 'https://www.linkedin.com/feed/update/urn:li:ugcPost:7384564293033340928',
        linkedinUrl: 'https://www.linkedin.com/in/ramiro-crespi'
    },
    {
        name: 'Fernando Benítez',
        role: 'Data Engineer',
        achievement: 'Superó expectativas y mejoró su comunicación con entrevistadores',
        quote: '"La asesoría superó mis expectativas. Me hiciste ver las cosas desde la forma de negocio, hablando con KPIs, resumiendo todo de forma más digerible para un entrevistador. Me dejó todo mucho más claro. Es muy fácil explicar aspectos técnicos, pero la forma en que comuniqué mis habilidades con ellos me va a servir bastante. Gracias a ti."',
        time: '1 mes',
        result: 'Mejor comunicación técnica',
        company: 'Data Engineer',
        videoUrl: '',
        linkedinUrl: ''
    },
    {
        name: 'Julián Mendelewicz',
        role: 'Ciencias sociales → Data Engineer en ~2 meses',
        achievement: 'Consiguió su primer trabajo como Data Engineer en ~2 meses',
        quote: '"Llegué por TikTok viendo el caso de una alumna que pasó de pastelera a Ingeniera de IA en 4 meses. Vi que los resultados eran reales y decidí invertir en mentoría personalizada para hacer mi propia transición."',
        time: '~2 meses',
        result: 'Data Engineer',
        company: 'Primer trabajo en datos',
        videoUrl: '',
        linkedinUrl: 'https://www.linkedin.com/posts/julian-mendelewicz_quer%C3%ADa-pasar-a-comentar-que-hace-unas-semanas-activity-7455687524775211010-OHVH/?utm_source=share&utm_medium=member_desktop&rcm=ACoAAByKCd4BfG9TAkC3uxx3f5i_TmDfdHHmHncQue'
    },
];

interface TestimonialsProps {
    /** Si true, layout compacto para la franja arriba del hero */
    compact?: boolean;
    /** Si true, acentos verdes para fondo oscuro (Matrix) */
    darkBg?: boolean;
}

/** Una línea corta para la barra: resultado + nombre */
function shortLine(t: TestimonialItem): string {
    const q = t.quote.replace(/^["']|["']$/g, '').trim();
    if (q.length <= 90) return q;
    return q.slice(0, 87).trim() + '…';
}

const CARD_WIDTH = 380;
const CARD_GAP = 20;
const SCROLL_STEP = CARD_WIDTH + CARD_GAP;

export default function Testimonials({ compact, darkBg }: TestimonialsProps) {
    const [activeIndex, setActiveIndex] = useState(0);
    const scrollRef = useRef<HTMLDivElement>(null);
    const testimonial = testimonials[activeIndex];

    useEffect(() => {
        if (!compact || !scrollRef.current) return;
        const el = scrollRef.current;
        const scroll = () => {
            const max = el.scrollWidth - el.clientWidth;
            if (max <= 0) return;
            const next = el.scrollLeft + SCROLL_STEP;
            if (next >= max) el.scrollTo({ left: 0, behavior: 'smooth' });
            else el.scrollTo({ left: next, behavior: 'smooth' });
        };
        const interval = setInterval(scroll, 5000);
        return () => clearInterval(interval);
    }, [compact]);

    useEffect(() => {
        if (!compact && testimonials.length > 1) {
            const interval = setInterval(() => {
                setActiveIndex((prev) => (prev + 1) % testimonials.length);
            }, 8000);
            return () => clearInterval(interval);
        }
    }, [compact]);

    const goNext = () => setActiveIndex((prev) => (prev + 1) % testimonials.length);
    const goPrev = () => setActiveIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);

    // Franja: cuadros con logro destacado, carousel que gira solo, sin barra abajo
    if (compact) {
        const headingCls = darkBg ? 'text-green-400' : 'text-slate-800';
        return (
            <section id="testimonios" className="py-3 md:py-4 w-full">
                <h2 className={`text-center text-xl md:text-2xl font-bold mb-3 md:mb-4 ${headingCls}`}>
                    Casos de éxito
                </h2>
                <div
                    ref={scrollRef}
                    className="w-full overflow-x-auto scroll-smooth snap-x snap-mandatory [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                    <div className="flex gap-5 pl-6 pr-6 min-w-max">
                        {testimonials.map((t, index) => {
                            const linkUrl = t.videoUrl || t.linkedinUrl;
                            const linkLabel = t.videoUrl ? (t.videoLabel || 'Ver video') : 'Ver en LinkedIn';
                            const CardWrapper = linkUrl ? 'a' : 'div';
                            const cardProps = linkUrl
                                ? { href: linkUrl, target: '_blank', rel: 'noopener noreferrer' }
                                : {};
                            const cardStyle = darkBg
                                ? 'border-green-500/40 bg-gray-900/90 hover:border-green-400/60'
                                : 'border-slate-200 bg-white hover:border-slate-300';
                            const titleStyle = darkBg ? 'text-white' : 'text-slate-800';
                            const descStyle = darkBg ? 'text-gray-300' : 'text-slate-600';
                            const nameStyle = darkBg ? 'text-white' : 'text-slate-800';
                            const linkStyle = darkBg ? 'text-green-400' : 'text-blue-600';
                            return (
                                <CardWrapper
                                    key={index}
                                    {...cardProps}
                                    className={`flex-shrink-0 w-[300px] sm:w-[340px] md:w-[360px] snap-center rounded-xl border shadow-sm px-4 py-3 text-left min-h-[5.5rem] flex flex-col justify-between ${cardStyle} ${linkUrl ? 'cursor-pointer hover:shadow-md transition-all' : ''}`}
                                >
                                    <div>
                                        {t.achievement && (
                                            <p className={`text-base md:text-lg font-bold mb-1.5 leading-tight ${titleStyle}`}>
                                                {t.achievement}
                                            </p>
                                        )}
                                        <p className={`text-sm md:text-base italic line-clamp-2 leading-snug ${descStyle}`}>
                                            {shortLine(t)}
                                        </p>
                                    </div>
                                    <div className="mt-2 flex items-center justify-between gap-2">
                                        <span className={`text-sm font-semibold truncate ${nameStyle}`}>{t.name}</span>
                                            {linkUrl && (
                                                <span className={`text-sm whitespace-nowrap ${linkStyle}`}>
                                                {linkLabel} →
                                            </span>
                                        )}
                                    </div>
                                </CardWrapper>
                            );
                        })}
                    </div>
                </div>
            </section>
        );
    }

    // Sección completa (scroll más abajo si se usa en otro lado)
    return (
        <section id="testimonios" className="py-24">
            <div className="max-w-6xl mx-auto px-6">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <div className="text-center mb-16">
                        <h2 className="text-4xl md:text-5xl font-bold text-slate-800 mb-6">Historias de Transformación</h2>
                        <p className="text-xl text-slate-600 max-w-3xl mx-auto">Resultados reales de personas que cambiaron su vida profesional</p>
                        <div className="w-20 h-1 bg-blue-500 mx-auto mt-6"></div>
                    </div>

                    <div className="relative max-w-3xl mx-auto">
                        <motion.div key={activeIndex} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="bg-white shadow-xl border border-slate-200 rounded-3xl p-8 md:p-12">
                            <div className="text-center space-y-6">
                                <div className="flex justify-center gap-1">
                                    {[...Array(5)].map((_, i) => (<Star key={i} className="w-6 h-6 fill-amber-400 text-amber-400" />))}
                                </div>
                                <div className="flex flex-wrap justify-center gap-3 mb-6">
                                    <div className="inline-flex items-center gap-2 bg-slate-50 text-slate-600 px-4 py-2 rounded-full text-sm font-medium border border-slate-200"><Clock className="w-4 h-4" />{testimonial.time}</div>
                                    <div className="inline-flex items-center gap-2 bg-slate-50 text-slate-600 px-4 py-2 rounded-full text-sm font-medium border border-slate-200"><Check className="w-4 h-4" />{testimonial.result}</div>
                                    <div className="inline-flex items-center gap-2 bg-slate-50 text-slate-600 px-4 py-2 rounded-full text-sm font-medium border border-slate-200"><Briefcase className="w-4 h-4" />{testimonial.company}</div>
                                </div>
                                <div className="relative">
                                    <Quote className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                                    <p className="text-xl text-slate-600 leading-relaxed italic">{testimonial.quote}</p>
                                </div>
                                <div className="pt-6 border-t border-slate-200">
                                    <p className="text-lg font-bold text-slate-800">{testimonial.name}</p>
                                    <p className="text-sm text-slate-500">{testimonial.role}</p>
                                    <div className="flex items-center justify-center gap-3 mt-4">
                                        {testimonial.videoUrl && (
                                            <a href={testimonial.videoUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-full text-sm font-medium transition-all shadow-md hover:shadow-lg">
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z" /></svg>{testimonial.videoLabel || 'Ver video'}
                                            </a>
                                        )}
                                        {testimonial.linkedinUrl && (
                                            <a href={testimonial.linkedinUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-blue-600 px-4 py-2 rounded-full text-sm font-medium transition-all border border-blue-600">
                                                <Linkedin className="w-4 h-4" />Ver en LinkedIn
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </motion.div>

                        <div className="flex justify-center gap-4 mt-8">
                            <button onClick={goPrev} className="w-12 h-12 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full flex items-center justify-center transition-all shadow-sm border border-slate-200"><ChevronLeft className="w-6 h-6" /></button>
                            <div className="flex items-center gap-2">
                                {testimonials.map((_, index) => (<button key={index} onClick={() => setActiveIndex(index)} className={`transition-all rounded-full ${index === activeIndex ? 'w-8 h-3 bg-blue-500' : 'w-3 h-3 bg-slate-300 hover:bg-slate-400'}`} />))}
                            </div>
                            <button onClick={goNext} className="w-12 h-12 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-full flex items-center justify-center transition-all shadow-sm border border-slate-200"><ChevronRight className="w-6 h-6" /></button>
                        </div>
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
